import { routePaths } from "../../routes/navigation";
import {
    onlineCockpitRouteAccessCodes,
    retailCockpitRouteAccessCodes,
} from "./cockpitTabPermissions";
import {
    campaignsRouteAccessCodes,
    clientsRouteAccessCodes,
    expensesAnyAccessCodes,
    expensesMarketingAccessCodes,
    expensesSalariesAccessCodes,
    expensesTransportAccessCodes,
    frequencyRouteAccessCodes,
    loyaltyRouteAccessCodes,
    ordersRouteAccessCodes,
    suppliersRouteAccessCodes,
} from "./routeAccessCodes";
import type { User } from "./types";

const analyticsPermissions = ["reports.view", "dashboard.management"];

export function hasPermission(user: User | null, permission: string) {
    if (!user?.isActive) {
        return false;
    }
    if (String(user.role ?? "").trim().toLowerCase() === "administrator") {
        return true;
    }
    return Boolean(user.permissions.includes(permission));
}

export function hasAnyPermission(user: User | null, permissions: string[]) {
    if (!user?.isActive) {
        return false;
    }
    if (String(user.role ?? "").trim().toLowerCase() === "administrator") {
        return true;
    }
    return permissions.some((permission) => user.permissions.includes(permission));
}

function isAlwaysAllowedPath(pathname: string) {
    return (
        pathname === routePaths.root
        || pathname === routePaths.changePassword
        || pathname === routePaths.messages
        || pathname.startsWith(`${routePaths.messages}/`)
    );
}

export function canAccessPath(user: User | null, pathname: string) {
    if (!user || !user.isActive) {
        return false;
    }

    if (isAlwaysAllowedPath(pathname)) {
        return true;
    }

    // Администратор: пълен достъп до всички екрани.
    if (String(user.role ?? "").trim().toLowerCase() === "administrator") {
        return true;
    }

    if (
        pathname === routePaths.adminAccess
        || pathname.startsWith(`${routePaths.adminAccess}/`)
    ) {
        return hasAnyPermission(user, ["users.manage", "roles.manage"]);
    }

    if (
        pathname === routePaths.clients
        || pathname.startsWith(`${routePaths.clients}/`)
        || pathname === routePaths.client360
        || pathname === routePaths.clientHistory
    ) {
        return hasAnyPermission(user, clientsRouteAccessCodes());
    }

    if (
        pathname === routePaths.orders
        || pathname.startsWith(`${routePaths.orders}/`)
        || pathname === routePaths.orderItems
    ) {
        return hasAnyPermission(user, ordersRouteAccessCodes());
    }

    if (pathname === routePaths.loyalty || pathname.startsWith(`${routePaths.loyalty}/`)) {
        return hasAnyPermission(user, loyaltyRouteAccessCodes());
    }

    if (pathname === routePaths.wholesaleSuppliers || pathname.startsWith(`${routePaths.wholesaleSuppliers}/`)) {
        return hasAnyPermission(user, suppliersRouteAccessCodes());
    }

    if (pathname === routePaths.wholesalePromotions || pathname.startsWith(`${routePaths.wholesalePromotions}/`)) {
        return hasAnyPermission(user, ["b2b.promotions.view", "b2b.promotions.manage", "b2b.view"]);
    }

    if (pathname === routePaths.b2b || pathname.startsWith(`${routePaths.b2b}/`)) {
        return hasPermission(user, "b2b.view");
    }

    if (pathname === routePaths.campaigns || pathname.startsWith(`${routePaths.campaigns}/`)) {
        return hasAnyPermission(user, campaignsRouteAccessCodes());
    }

    if (
        pathname === routePaths.bi
        || pathname.startsWith(`${routePaths.bi}/`)
        || pathname === routePaths.grafica
        || pathname.startsWith(`${routePaths.grafica}/`)
        || pathname === routePaths.grafica2
        || pathname.startsWith(`${routePaths.grafica2}/`)
    ) {
        return hasAnyPermission(user, ["bi.traders.view", "bi.traders.manage", ...analyticsPermissions]);
    }

    if (
        pathname === routePaths.retailCockpit
        || pathname.startsWith(`${routePaths.retailCockpit}/`)
        || pathname === routePaths.retailFrequency
        || pathname.startsWith(`${routePaths.retailFrequency}/`)
        || pathname === routePaths.retailPercentTargets
        || pathname.startsWith(`${routePaths.retailPercentTargets}/`)
        || pathname === routePaths.retailDefectsSliven
        || pathname.startsWith(`${routePaths.retailDefectsSliven}/`)
    ) {
        if (pathname === routePaths.retailPercentTargets || pathname.startsWith(`${routePaths.retailPercentTargets}/`)) {
            return hasAnyPermission(user, [
                "retail.percent_targets.view",
                "retail.percent_targets.manage",
            ]);
        }

        if (pathname === routePaths.retailDefectsSliven || pathname.startsWith(`${routePaths.retailDefectsSliven}/`)) {
            return hasAnyPermission(user, [
                "retail.defects_sliven.view",
                "retail.defects_sliven.manage",
            ]);
        }

        if (pathname === routePaths.retailFrequency || pathname.startsWith(`${routePaths.retailFrequency}/`)) {
            return hasAnyPermission(user, frequencyRouteAccessCodes());
        }

        return hasAnyPermission(user, retailCockpitRouteAccessCodes());
    }

    if (
        pathname === routePaths.onlineCockpit
        || pathname.startsWith(`${routePaths.onlineCockpit}/`)
    ) {
        return hasAnyPermission(user, onlineCockpitRouteAccessCodes());
    }

    if (pathname === routePaths.dashboard || pathname.startsWith(`${routePaths.dashboard}/`)) {
        if (pathname === routePaths.dashboardWorkspace || pathname.startsWith(`${routePaths.dashboardWorkspace}/`)) {
            return hasAnyPermission(user, [
                "warehouse.workspace.view",
                "warehouse.workspace.manage",
            ]);
        }

        return hasAnyPermission(user, [
            "warehouse.dashboard.view",
            "warehouse.dashboard.manage",
        ]);
    }

    if (pathname === routePaths.expenses || pathname.startsWith(`${routePaths.expenses}/`)) {
        if (pathname.startsWith(routePaths.expensesMarketing)) {
            return hasAnyPermission(user, expensesMarketingAccessCodes());
        }
        if (pathname.startsWith(routePaths.expensesSalaries)) {
            return hasAnyPermission(user, expensesSalariesAccessCodes());
        }
        if (
            pathname === routePaths.expenses
            || pathname.startsWith(routePaths.expensesTransport)
        ) {
            return hasAnyPermission(user, expensesTransportAccessCodes());
        }
        return hasAnyPermission(user, expensesAnyAccessCodes());
    }

    // Непознат път: отказ по подразбиране (без „отворено“ меню).
    return false;
}
